import { isEqual } from "lodash";
import type { AllocationObj, TimeRangeFinite } from "types/types";
import { PnlCategoriesEnum } from "types/enums";
import {
  plannedTagColor,
  unplannedTagColor,
  waitingTagColor,
  notScheduledTagColor,
} from "constants/colors";

export type TagTree = {
  tagId: string;
  subTags: Tag[];
};

export type Tag = {
  tagId: string;
  subTags?: Tag[];
};

export type TagInfo = {
  id: string;
  messageShort: string;
  messageLong: string;
  gracePeriod: number;
  targetPeriod: number;
  pnlCategory: string;
  visible: boolean;
  offlineTag: boolean;
  textColor: string;
  color: string;
  queueable: boolean;
  icon?: string;
  priority?: string;
};

export type TagWithInfo = {
  tagId: string;
  subTags?: TagWithInfo[];
  tagInfo: TagInfo;
  category: string;
};

export type HubletTag = {
  tagName: string;
  tagPath: string[];
  color: string;
  textColor: string;
  icon: string | null;
  category: string;
  subTags?: HubletTag[] | null;
};

export const areArraysEqual = (x, y) => isEqual(x.sort(), y.sort());

export const getAllocationTotalNumMinutes = (
  allocationList: AllocationObj[]
): number => {
  return allocationList.reduce(
    (acc, allocationObj) => acc + allocationObj.numberOfMinutes,
    0
  );
};

/**
 * getNewFormState replaces the number of minutes in a specific formStateObj
 * within the formState array
 *
 * @param {{
 *   category: string;
 *   tagName: string;
 *   newMinutes: number;
 *   formState: SupervisorContextWithDateTimeType[];
 * }} {
 *   category,    pnl category of the new formState object
 *   tagName,     name of tag being replaced in formState
 *   newMinutes,  new number of minutes for the formState object
 *   formState    current formState array
 * }
 * @returns  new formState obj, with new number of minutes
 */
export const getNewFormState = ({
  category,
  tagName,
  newMinutes,
  timeRange,
  processId,
  formState,
}: {
  category: string;
  tagName: string;
  newMinutes: number;
  timeRange: TimeRangeFinite;
  processId: string;
  formState: AllocationObj[];
}): AllocationObj[] => {
  // find index of formState obj we are changing within formState arrau
  const formStateObjIndex = formState.findIndex(x => x.tagName === tagName);
  if (formStateObjIndex >= 0) {
    const formStateObj = formState[formStateObjIndex];

    // replace the number of minutes with the new number of minutes in the formState obj
    const newFormStateObject = {
      ...formStateObj,
      numberOfMinutes: newMinutes,
    };

    // replace the original object in formState arr
    return [
      ...formState.slice(0, formStateObjIndex),
      newFormStateObject,
      ...formState.slice(formStateObjIndex + 1),
    ];
  } else {
    // if tag being added was new, just add it to the formState arr
    return [
      ...formState,
      {
        range: timeRange,
        processId,
        category,
        tagName,
        numberOfMinutes: 0,
        isChangeover: false,
      },
    ];
  }
};

export const getHasSupervisorContext = formState => {
  return formState.length > 0;
};

export const getFormDataToSubmit = (
  date,
  startTime,
  endTime,
  formState
): FormData => {
  const formData = new FormData();
  formData.append("Date", date);
  formData.append("StartTime", startTime);
  formData.append("EndTime", endTime);

  const categoryArr = [] as string[];
  const tagNameArr = [] as string[];
  const numMinArr = [] as string[];
  const changeoverArr = [] as string[];

  formState.forEach(({ category, tagName, numberOfMinutes, isChangeover }) => {
    categoryArr.push(category);
    tagNameArr.push(tagName);
    numMinArr.push(`${numberOfMinutes}`);
    changeoverArr.push(`${isChangeover}`);
  });
  formData.append("Category", categoryArr.toString());
  formData.append("TagName", tagNameArr.toString());
  formData.append("NumberOfMinutes", numMinArr.toString());
  formData.append("Changeover", changeoverArr.toString());

  return formData;
};

export const TAG_COLOR_TO_PNL_CATEGORY = {
  [plannedTagColor]: PnlCategoriesEnum.planned,
  [unplannedTagColor]: PnlCategoriesEnum.unplanned,
  [waitingTagColor]: PnlCategoriesEnum.waiting,
  [notScheduledTagColor]: PnlCategoriesEnum.notScheduled,
};

export const handleEditClick = ({
  isEditable,
  setIsEditable,
  completedFormStateRef,
  setFormState,
}) => {
  if (isEditable && completedFormStateRef.current !== null) {
    setFormState(completedFormStateRef.current);
  }

  // form should stay editable if there was no previous input
  if (!getHasSupervisorContext(completedFormStateRef.current)) {
    setIsEditable(true);
  } else {
    setIsEditable(!isEditable);
  }
};
